import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const input = "w-full border border-white/15 bg-white/5 text-ivory px-4 py-3 text-sm outline-none focus:border-champagne transition-colors placeholder:text-linen/40";

export default function AdminLogin() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("magic"); // magic | password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const requestLink = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try { await api.post("/auth/admin/request-magic-link", { email }); setSent(true); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const passwordLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const u = await login(email, password);
      if (u.role !== "admin") { setError("Access denied. This account is not authorised for the administrator dashboard."); return; }
      navigate("/admin");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#141312] px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-12 h-12 rounded-full border border-champagne/40 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-5 h-5 text-champagne" />
          </div>
          <div className="font-serif text-3xl tracking-[0.3em] text-ivory">LUXYREIGN</div>
          <p className="eyebrow text-linen/50 mt-4">Administrator Portal</p>
        </div>

        {error && <div className="bg-vermilion/15 border border-vermilion/40 text-vermilion text-sm px-4 py-3 mb-4" data-testid="admin-login-error">{error}</div>}

        {mode === "magic" ? (
          sent ? (
            <div className="bg-emeraldauth/15 border border-emeraldauth/40 text-linen text-sm px-5 py-6 text-center" data-testid="admin-magic-sent">
              <p className="text-ivory font-serif text-xl mb-2">Check your inbox</p>
              <p className="text-linen/70">If this email is authorised, a secure single-use sign-in link has been sent. It expires in 15 minutes.</p>
            </div>
          ) : (
            <form onSubmit={requestLink} className="space-y-4">
              <p className="text-sm text-linen/60 text-center mb-2">Enter your authorised email to receive a secure sign-in link.</p>
              <input required type="email" placeholder="Authorised email address" value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="admin-magic-email" />
              <button type="submit" disabled={loading} data-testid="admin-magic-submit"
                className="w-full bg-champagne text-pitch text-xs font-mono uppercase tracking-[0.2em] py-4 hover:bg-champagne-soft transition-colors disabled:opacity-50">
                {loading ? "Sending…" : "Email Me a Sign-In Link"}
              </button>
            </form>
          )
        ) : (
          <form onSubmit={passwordLogin} className="space-y-4">
            <input required type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="admin-password-email" />
            <input required type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} data-testid="admin-password-input" />
            <button type="submit" disabled={loading} data-testid="admin-password-submit"
              className="w-full bg-champagne text-pitch text-xs font-mono uppercase tracking-[0.2em] py-4 hover:bg-champagne-soft transition-colors disabled:opacity-50">
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        )}

        <button onClick={() => { setMode(mode === "magic" ? "password" : "magic"); setSent(false); setError(""); }}
          data-testid="admin-toggle-mode"
          className="w-full text-center text-xs text-linen/50 hover:text-champagne mt-6 uppercase tracking-[0.15em] transition-colors">
          {mode === "magic" ? "Sign in with password instead" : "Email me a sign-in link instead"}
        </button>
      </div>
    </div>
  );
}
